/**
 * chronos-cart-bulk-sync.js v1.0
 *
 * Companion to chronos-bulk-pricing.js. That script's "Order in Bulk" flow
 * creates a Shopify discount code for a FIXED rupee amount, sized for the
 * exact quantity at the moment of checkout — it does not know about, or
 * auto-adjust for, a later quantity edit made in the cart or cart drawer.
 * Without this script, reducing quantity after the fact keeps the full
 * fixed discount applied to a smaller line total (undercharging), and
 * increasing quantity keeps a discount sized for the smaller amount
 * (overcharging relative to what the tier table promised).
 *
 * This script:
 *   1. Reads the {variant_id, quantity, discount_code} that
 *      chronos-bulk-pricing.js stored in sessionStorage when the discount
 *      was applied.
 *   2. Watches for cart quantity changes by wrapping window.fetch and
 *      watching for calls to Shopify's standard cart endpoints
 *      (/cart/change.js, /cart/update.js, /cart/add.js, /cart/clear.js).
 *      This is deliberately endpoint-based rather than DOM-selector-based —
 *      it works regardless of this theme's specific cart/cart-drawer
 *      markup, since every AJAX cart update ultimately goes through one of
 *      these endpoints. A full-page form-submit cart update (no fetch)
 *      still gets caught for free, because it results in a fresh page load
 *      and this script re-checks the moment it boots.
 *   3. On a detected change, compares the CURRENT quantity of that variant's
 *      cart line (via /cart.js) against the quantity the discount was sized
 *      for:
 *        - Line no longer exists (removed / cart cleared) → clear the
 *          discount session entirely.
 *        - Quantity changed → ask the worker for a fresh discount code
 *          sized to the new quantity (reuses the exact same
 *          /public/bulk-discount endpoint chronos-bulk-pricing.js uses —
 *          no worker changes needed). If the new quantity no longer
 *          qualifies for any tier, the worker returns 400 and we just clear
 *          the discount instead.
 *   4. Applies the change via /discount/NONE then /discount/<code>, run as
 *      background fetches (NOT full-page navigations) so this doesn't
 *      interrupt whatever the customer is doing.
 *   5. Refreshes what's on screen: dispatches the same
 *      'dispatch:cart-drawer:refresh' event chronos-addons.js already
 *      relies on (so the drawer re-renders with the corrected total), and
 *      if the customer is on the actual /cart page, does a plain reload so
 *      the server-rendered total is guaranteed correct rather than this
 *      script guessing at DOM structure it doesn't know.
 *
 * Must be loaded SITE-WIDE (e.g. from theme.liquid / the main layout), not
 * just on the product template — the cart drawer can be opened from any
 * page, and a quantity edit inside it needs to be caught there too.
 *
 * One known side effect: because each resync creates a brand-new single-use
 * discount code via the worker, rapid or repeated quantity edits will leave
 * a handful of unused BULK- codes behind in Shopify Admin (each already
 * expires itself after 2 hours per the worker's endsAt). Cosmetic only —
 * they're single-use and time-limited, so they can't be reused or stacked.
 */
(function () {
  'use strict';

  // ⚠️ Keep this in sync with WORKER_URL in chronos-bulk-pricing.js — same
  // worker, same value.
  var WORKER_URL = 'https://chronos-proxy-worker.orange-frog-2d58.workers.dev';

  var STORAGE_KEY   = 'chronos_bulk_discount';
  var DEBOUNCE_MS   = 700; // let rapid +/- clicks settle before hitting the worker
  var CART_ENDPOINT_RE = /\/cart\/(change|update|add|clear)(\.js)?(\?|$)/;

  var _debounceTimer = null;
  var _syncing       = false; // reentrancy guard — resync's own /cart/add etc. calls should not re-trigger itself

  function getTracked() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function setTracked(entry) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry)); }
    catch (e) { /* ignore — non-fatal, just means we won't catch the NEXT edit either */ }
  }

  function clearTracked() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function clearDiscountSession() {
    return fetch('/discount/NONE', { redirect: 'manual' }).catch(function () {});
  }

  function applyDiscountSession(code) {
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart', {
      credentials: 'same-origin',
    }).catch(function () {});
  }

  function refreshCartUI() {
    // Best-effort broadcast — covers this theme's known event contract
    // (chronos-addons.js already dispatches/relies on the drawer-refresh
    // event) plus a couple of common alternates, all harmless if unused.
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));

    if (window.location.pathname.indexOf('/cart') === 0) {
      // Full cart page — reload so the server-rendered total is guaranteed
      // correct, rather than guessing at this theme's DOM structure.
      window.location.reload();
    }
  }

  function requestFreshDiscount(variantId, quantity) {
    return fetch(WORKER_URL + '/public/bulk-discount', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ variant_id: variantId, quantity: quantity }),
    }).then(function (resp) {
      return resp.json().then(function (data) {
        return { ok: resp.ok, data: data };
      });
    });
  }

  function resync() {
    if (_syncing) return;
    var tracked = getTracked();
    if (!tracked || !tracked.variant_id) return; // nothing of ours to watch

    _syncing = true;

    getCart().then(function (cart) {
      var line = null;
      for (var i = 0; i < cart.items.length; i++) {
        if (String(cart.items[i].variant_id) === String(tracked.variant_id)) {
          line = cart.items[i];
          break;
        }
      }

      // Line is gone (removed, or cart cleared) — nothing left to discount.
      if (!line) {
        clearTracked();
        return clearDiscountSession().then(refreshCartUI);
      }

      // Quantity unchanged — the existing code is still correctly sized.
      if (line.quantity === tracked.quantity) {
        _syncing = false;
        return;
      }

      // Quantity changed — get a discount sized for the new quantity.
      return requestFreshDiscount(tracked.variant_id, line.quantity).then(function (result) {
        if (result.ok && result.data && result.data.discount_code) {
          setTracked({
            variant_id:    tracked.variant_id,
            quantity:      line.quantity,
            discount_code: result.data.discount_code,
          });
          return clearDiscountSession()
            .then(function () { return applyDiscountSession(result.data.discount_code); })
            .then(refreshCartUI);
        }

        // New quantity doesn't qualify for any tier (or the request
        // failed) — fall back to no discount rather than leave a
        // mis-sized one applied.
        clearTracked();
        return clearDiscountSession().then(refreshCartUI);
      });
    })
    .catch(function (err) {
      console.error('[chronos-cart-bulk-sync] resync failed:', err);
    })
    .finally(function () {
      _syncing = false;
    });
  }

  function scheduleResync() {
    if (_debounceTimer) clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(resync, DEBOUNCE_MS);
  }

  // ── Catch AJAX cart updates regardless of this theme's markup ──────────────
  var _origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var href = (typeof url === 'string') ? url : (url && url.url) || '';
    var isCartWrite = CART_ENDPOINT_RE.test(href);
    var result = _origFetch.apply(this, arguments);
    if (isCartWrite) {
      result.then(scheduleResync).catch(function () {});
    }
    return result;
  };

  // ── Catch full-page cart updates / other themes' own refresh events ────────
  // Harmless no-ops if this theme never fires them; scheduleResync() itself
  // is a no-op when there's nothing tracked in sessionStorage.
  ['cart:updated', 'cart:refresh', 'dispatch:cart-drawer:refresh'].forEach(function (evt) {
    document.addEventListener(evt, scheduleResync);
  });

  // ── Check once on load — catches a quantity change made via a full
  // page-reload form submit, which the fetch wrapper above can't see. ──────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resync);
  } else {
    resync();
  }
})();
